import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/api';

const moodOptions = [
  { emoji: '😊', label: 'Happy', value: 'happy' },
  { emoji: '😢', label: 'Sad', value: 'sad' },
  { emoji: '😡', label: 'Angry', value: 'angry' },
  { emoji: '😴', label: 'Tired', value: 'tired' },
  { emoji: '😎', label: 'Cool', value: 'cool' },
  { emoji: '🤔', label: 'Thoughtful', value: 'thoughtful' },
  { emoji: '😍', label: 'Loving', value: 'loving' },
  { emoji: '😱', label: 'Stressed', value: 'stressed' },
];

function App() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState({
    date: new Date(),
    content: '',
    mood: 'happy'
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async (search = '') => {
    try {
      setLoading(true);
      const url = search 
        ? `${API_URL}/entries?search=${encodeURIComponent(search)}`
        : `${API_URL}/entries`;
      const response = await axios.get(url);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.content.trim()) return;

    try {
      const entryToSubmit = {
        ...newEntry,
        date: format(newEntry.date, 'yyyy-MM-dd')
      };

      const response = await axios.post(`${API_URL}/entries`, entryToSubmit);
      setEntries([response.data, ...entries]);
      setNewEntry({
        date: new Date(),
        content: '',
        mood: 'happy'
      });
    } catch (error) {
      console.error('Error creating entry:', error);
      alert('Failed to save entry. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await axios.delete(`${API_URL}/entries/${id}`);
      setEntries(entries.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry.');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEntries(searchTerm);
  };

  const clearSearch = () => {
    setSearchTerm('');
    fetchEntries();
  };

  const formatDate = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
  };

  const getMoodEmoji = (moodValue) => {
    const mood = moodOptions.find(m => m.value === moodValue);
    return mood ? mood.emoji : '😊';
  };

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <h1><i className="fas fa-book"></i> Daily Journal</h1>
          <p className="subtitle">Capture your thoughts, feelings, and memories</p>
        </div>
      </header>

      <main className="container main-content">
        <div className="app-grid">
          {/* Entry Form */}
          <div className="entry-form-container">
            <div className="card">
              <h2><i className="fas fa-pen"></i> New Entry</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label><i className="fas fa-calendar"></i> Date</label>
                  <DatePicker
                    selected={newEntry.date}
                    onChange={(date) => setNewEntry({...newEntry, date})}
                    dateFormat="MMMM d, yyyy"
                    className="date-picker"
                    maxDate={new Date()}
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-smile"></i> How are you feeling?</label>
                  <div className="mood-selector">
                    {moodOptions.map((mood) => (
                      <button
                        key={mood.value}
                        type="button"
                        className={`mood-option ${newEntry.mood === mood.value ? 'selected' : ''}`}
                        onClick={() => setNewEntry({...newEntry, mood: mood.value})}
                        title={mood.label}
                      >
                        {mood.emoji}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label><i className="fas fa-edit"></i> What's on your mind?</label>
                  <textarea
                    value={newEntry.content}
                    onChange={(e) => setNewEntry({...newEntry, content: e.target.value})}
                    placeholder="Write about your day, thoughts, or anything you want to remember..."
                    rows="6"
                    required
                  />
                </div>

                <button type="submit" className="submit-btn">
                  <i className="fas fa-save"></i> Save Entry
                </button>
              </form>
            </div>
          </div>

          {/* Entries List */}
          <div className="entries-container">
            <div className="card">
              <div className="entries-header">
                <h2><i className="fas fa-history"></i> Journal Timeline</h2>
                <form onSubmit={handleSearch} className="search-form">
                  <div className="search-input-group">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search entries..."
                      className="search-input"
                    />
                    <button type="submit" className="search-btn">
                      <i className="fas fa-search"></i>
                    </button>
                    {searchTerm && (
                      <button type="button" onClick={clearSearch} className="clear-search-btn">
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {loading ? (
                <div className="loading">
                  <i className="fas fa-spinner fa-spin"></i> Loading entries...
                </div>
              ) : entries.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-book-open"></i>
                  <h3>No entries yet</h3>
                  <p>{searchTerm ? 'No entries match your search' : 'Start by writing your first journal entry!'}</p>
                </div>
              ) : (
                <div className="timeline">
                  {entries.map((entry) => (
                    <div key={entry.id} className="timeline-item">
                      <div className="timeline-marker">
                        <span className="mood-display">{getMoodEmoji(entry.mood)}</span>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <h3>{formatDate(entry.date)}</h3>
                          <button
                            onClick={() => handleDelete(entry.id)}
                            className="delete-btn"
                            title="Delete entry"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                        <div className="timeline-body">
                          <p>{entry.content}</p>
                        </div>
                        <div className="timeline-footer">
                          <span className="time">
                            <i className="far fa-clock"></i> {format(parseISO(entry.created_at), 'h:mm a')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>Daily Journal App • Built with React & Node.js</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
